/**
 * MarkDeclinedDialog — quote status manual override (2026-07-24).
 *
 * Records an out-of-band decline (the client said no verbally or by email).
 * Reason is mandatory — it renders as "Reason from client" on the quote's
 * Declined banner. The transition releases the quote's tentative schedule
 * holds; when live COMMITTED bookings would be released the backend refuses
 * with 409 SOURCE_RELEASES_COMMITTED and this dialog surfaces an in-place
 * "Release & continue" confirm (same ceremony as the Mark Expired door).
 */
import { useState } from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, TextField, Typography,
} from '@mui/material';
import {
  markQuoteDeclined,
  type MarkDeclinedChannel,
  type StatusChangeResponse,
} from '../../../services/quoteService';

/** Single source for the channel options — no magic strings in the JSX. */
const CHANNEL_OPTIONS: ReadonlyArray<{ value: MarkDeclinedChannel; label: string }> = [
  { value: 'verbal', label: 'Verbal' },
  { value: 'email', label: 'Email' },
  { value: 'other', label: 'Other' },
];

interface Props {
  open: boolean;
  quoteKey: string;
  onClose: () => void;
  /** Called after a successful decline — the parent refetches + shows release info. */
  onDeclined: (result: StatusChangeResponse) => void;
}

/** Committed-work refusal details from the 409 SOURCE_RELEASES_COMMITTED body. */
interface CommittedWarning {
  message: string;
  committedBookings: number;
  taskNames: string[];
}

export const MarkDeclinedDialog = ({ open, quoteKey, onClose, onDeclined }: Props) => {
  const [channel, setChannel] = useState<MarkDeclinedChannel>('verbal');
  const [reason, setReason] = useState('');
  const [declinedByName, setDeclinedByName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [committedWarning, setCommittedWarning] = useState<CommittedWarning | null>(null);

  const reset = () => {
    setChannel('verbal'); setReason(''); setDeclinedByName('');
    setError(null); setCommittedWarning(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async (releaseSchedule = false) => {
    if (!reason.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await markQuoteDeclined(quoteKey, {
        channel,
        reason: reason.trim(),
        declinedByName: declinedByName.trim() || undefined,
      }, releaseSchedule ? { releaseSchedule: true } : undefined);
      reset();
      onDeclined(result);
    } catch (err) {
      const resp = (err as {
        response?: {
          status?: number;
          data?: { code?: string; error?: string; committedBookings?: number; taskNames?: string[] };
        };
      }).response;
      // P2-4 ceremony: committed work attached — confirm the release in place.
      if (resp?.status === 409 && resp.data?.code === 'SOURCE_RELEASES_COMMITTED') {
        setCommittedWarning({
          message: resp.data.error ?? 'Declining will release committed bookings from the live schedule.',
          committedBookings: resp.data.committedBookings ?? 0,
          taskNames: resp.data.taskNames ?? [],
        });
      } else {
        setError(resp?.data?.error
          ?? (err instanceof Error ? err.message : 'Failed to mark the quote declined.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Mark Quote as Declined</DialogTitle>
      <DialogContent>
        <TextField label="Reason from client" fullWidth required multiline minRows={2}
          size="small" autoFocus value={reason}
          onChange={(e) => setReason(e.target.value)} sx={{ mt: 1 }}
          placeholder="e.g. went with another lab, project cancelled" />
        <TextField label="How did they decline?" fullWidth required select size="small"
          value={channel}
          onChange={(e) => setChannel(e.target.value as MarkDeclinedChannel)}
          sx={{ mt: 2 }}>
          {CHANNEL_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <TextField label="Who declined (optional)" fullWidth size="small"
          value={declinedByName} onChange={(e) => setDeclinedByName(e.target.value)}
          sx={{ mt: 2 }} placeholder="Client contact name" />
        <Alert severity="info" sx={{ mt: 2 }}>
          The quote's tentative schedule holds will be released — machines and
          people become available again.
        </Alert>
        {committedWarning && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Live schedule attached
            </Typography>
            <Typography variant="body2">
              Declining will release {committedWarning.committedBookings} committed
              booking(s) from the live schedule
              {committedWarning.taskNames.length > 0
                ? ` (${committedWarning.taskNames.join(', ')})`
                : ''}. This cannot be undone.
            </Typography>
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        {committedWarning ? (
          <Button variant="contained" color="error" onClick={() => handleSubmit(true)}
            disabled={submitting}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
            {submitting ? <CircularProgress size={18} /> : 'Release & continue'}
          </Button>
        ) : (
          <Button variant="contained" color="error" onClick={() => handleSubmit(false)}
            disabled={submitting || !reason.trim()}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
            {submitting ? <CircularProgress size={18} /> : 'Mark as Declined'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
